import React from "react";
import {
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Card,
  Skeleton,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { green, red } from "@mui/material/colors";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import useStyles from "./Profile.styles";
import { useUserContext } from "../../../context/UserContext";
import { formatDate } from "../../../utils/utils";
import ProfilePictureUploader from "./ProfilePictureUploader.component";

const Profile = () => {
  const { classes } = useStyles();
  const { selectedUserDetailLoading, selectedUserDetail } = useUserContext();
  const {
    email,
    firstName,
    lastName,
    lanId,
    requestType,
    lastLoginDate,
    regions,
    active,
    admin,
    applicationAdministrator,
    globalTechSupport,
    userAccessProvisioner,
  } = selectedUserDetail || {};
  return (
    <div className={classes.page}>
      {selectedUserDetailLoading ? (
        <>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={56}
            animation="pulse"
            style={{ marginBottom: 8 }}
          />
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height={40}
              animation="pulse"
              style={{ marginBottom: 8 }}
            />
          ))}
        </>
      ) : (
        <Grid container justifyContent="center" alignItems="center">
          <Card
            style={{
              padding: "16px",
              width: "50%",
            }}
          >
            <Typography
              variant="h3"
              component="p"
              sx={{
                color: "#2f4f4f",
                fontSize: "1.8rem",
                textAlign: "center",
                marginBottom: "10px",
                fontWeight: "bold",
                padding: "10px 0",
                "&:hover": {
                  color: "gray",
                },
              }}
            >
              Welcome to Global TreasuryFin Suite
            </Typography>
            <Typography
              variant="subtitle1"
              component="p"
              sx={{
                color: "#555",
                fontSize: "1rem",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              Available for every Region, SFC, Department, and Team. Delivers
              automated, efficient, and scalable modeling. Streamlines reporting
              and collaboration. Secure, interactive, and intelligent.
            </Typography>
            <Divider sx={{ borderBottomWidth: 4, mb: 4, borderColor: "red" }} />
            <Grid item xs={12} sm={4} sx={{ textAlign: "center" }}>
              <ProfilePictureUploader />
              <Typography
                variant="h6"
                sx={{
                  mt: 2,
                  color: "primary.main",
                  fontWeight: "bold",
                  letterSpacing: "0.05em",
                }}
              >
                {firstName} {lastName}{" "}
                {admin && (
                  <StarIcon
                    sx={{
                      fontSize: "22px",
                      marginRight: "15px",
                      marginBottom: "-4px",
                      color: "#ffd600",
                    }}
                  />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {email}
              </Typography>
            </Grid>
            <br />
            <Grid
              container
              direction="column"
              spacing={2}
              sx={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "16px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <Grid item>
                <InfoGridRow label="First Name" value={firstName} />
              </Grid>
              <Grid item>
                <InfoGridRow label="Last Name" value={lastName} />
              </Grid>
              <Grid item>
                <InfoGridRow label="Email" value={email} />
              </Grid>
              <Grid item>
                <InfoGridRow label="Lan Id" value={lanId} />
              </Grid>
              <Grid item>
                <InfoGridRow label="Request Type" value={requestType} />
              </Grid>
              <Grid item>
                <InfoGridRow label="Active" value={active} isIcon />
              </Grid>
              <Grid item>
                <InfoGridRow label="Admin" value={admin} isIcon />
              </Grid>
              <Grid item>
                <InfoGridRow
                  label="Application Administrator"
                  value={applicationAdministrator}
                  isIcon
                />
              </Grid>
              <Grid item>
                <InfoGridRow
                  label="Global Tech Support"
                  value={globalTechSupport}
                  isIcon
                />
              </Grid>
              <Grid item>
                <InfoGridRow
                  label="User Access Provisioner"
                  value={userAccessProvisioner}
                  isIcon
                />
              </Grid>
              <Grid item>
                <InfoGridRow
                  label="Last Login"
                  value={formatDate(lastLoginDate)}
                />
              </Grid>
            </Grid>
            <Typography gutterBottom sx={{ mt: 4 }}>
              Region & Business Entity IDs
            </Typography>
            <Divider sx={{ mt: 0.5 }} />
            <Table size="small" aria-label="region-sfc-table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Region Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Business Entity ID</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Local Currency</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Country</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Readable</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Writable</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {regions?.map((region) =>
                  region?.sfcs?.map((sfc, idx) => (
                    <TableRow key={`${region?.regionId}-${sfc?.sfcId}`}>
                      <TableCell>{region?.regionName}</TableCell>
                      <TableCell>{sfc?.businessEntityID}</TableCell>
                      <TableCell>{sfc?.localCurrency}</TableCell>
                      <TableCell>{sfc?.country}</TableCell>
                      <TableCell>
                        <CheckIcon sx={{ color: green[500] }} />
                      </TableCell>
                      <TableCell>
                        {sfc?.writable ? (
                          <CheckIcon sx={{ color: green[500] }} />
                        ) : (
                          <CloseIcon sx={{ color: red[400] }} />
                        )}
                      </TableCell>
                    </TableRow>
                  )),
                )}
              </TableBody>
            </Table>
          </Card>
        </Grid>
      )}
    </div>
  );
};

const InfoGridRow = ({ label, value, isIcon = false }) => (
  <Grid container alignItems="center" spacing={1} wrap="nowrap">
    <Grid item sx={{ width: 150, pl: 2 }}>
      <Typography variant="body1" fontWeight="bold">
        {label}
      </Typography>
    </Grid>
    <Grid item sx={{ width: 10 }}>
      <Typography variant="body1">:</Typography>
    </Grid>
    <Grid item xs>
      {isIcon ? (
        value ? (
          <CheckCircleIcon color="success" />
        ) : (
          <CancelIcon color="error" />
        )
      ) : (
        <Typography variant="body1">{value || "N/A"}</Typography>
      )}
    </Grid>
  </Grid>
);
export default Profile;
